import { Gauge } from "lucide-react";

import { useT } from "../../i18n";
import type { Dictionary } from "../../i18n/ru";
import type { PlumeGrade } from "../../data/types";
import { cx } from "../../lib/cx";
import { useLayersStore } from "../../store/layersStore";
import { FloatingPanel } from "../layout/FloatingPanel";

interface Row {
  grade: PlumeGrade;
  /** CSS variable holding this grade's token color. */
  colorVar: string;
  /** Key into `t.legend`; resolved at render so it follows the language. */
  labelKey: keyof Dictionary["legend"];
}

const ROWS: Row[] = [
  { grade: "low", colorVar: "--plume-low", labelKey: "low" },
  { grade: "moderate", colorVar: "--plume-moderate", labelKey: "moderate" },
  { grade: "high", colorVar: "--plume-high", labelKey: "high" },
  { grade: "severe", colorVar: "--plume-severe", labelKey: "severe" },
  { grade: "unknown", colorVar: "--plume-unknown", labelKey: "unknown" },
];

export function GradientLegend() {
  const t = useT();
  const grades = useLayersStore((s) => s.grades);
  const toggleGrade = useLayersStore((s) => s.toggleGrade);
  const clearGrades = useLayersStore((s) => s.clearGrades);

  const filtering = grades.length > 0;
  const showAll = filtering ? (
    <button
      type="button"
      onClick={clearGrades}
      className="rounded px-1.5 py-0.5 text-xs font-medium text-accent hover:bg-accent-subtle"
    >
      {t.legend.showAll}
    </button>
  ) : null;

  return (
    <FloatingPanel
      icon={<Gauge size={16} />}
      title={t.legend.title}
      openLabel={t.legend.open}
      collapseLabel={t.legend.collapse}
      defaultOpen
      headerAccessory={showAll}
      widthClass="w-56"
    >
      <ul className="flex flex-col">
        {ROWS.map((row) => {
          const active = grades.includes(row.grade);
          const dimmed = filtering && !active;
          return (
            <li key={row.grade}>
              <button
                type="button"
                role="checkbox"
                aria-checked={active}
                onClick={() => toggleGrade(row.grade)}
                className={cx(
                  "flex w-full items-center gap-2 rounded px-1.5 py-1 text-left text-sm transition-colors",
                  active ? "bg-accent-subtle" : "hover:bg-surface-sunken",
                  dimmed && "opacity-45",
                )}
              >
                {/* Checkbox marker so multi-select reads clearly; the color
                    swatch sits inside it when checked. */}
                <span
                  className={cx(
                    "grid h-4 w-4 shrink-0 place-items-center rounded border transition-colors",
                    active ? "border-accent" : "border-border-strong",
                  )}
                >
                  <span
                    aria-hidden
                    className={cx(
                      "h-2.5 w-2.5 rounded-sm transition-opacity",
                      active ? "opacity-100" : "opacity-0",
                    )}
                    style={{ background: `var(${row.colorVar})` }}
                  />
                </span>
                {/* Always-visible swatch so the legend still maps color→range
                    even when the row isn't selected. */}
                <span
                  aria-hidden
                  className="h-3 w-3 shrink-0 rounded-sm border border-border-subtle"
                  style={{ background: `var(${row.colorVar})` }}
                />
                <span className="flex-1 text-text-primary">{t.legend[row.labelKey]}</span>
              </button>
            </li>
          );
        })}
      </ul>
      <p className="mt-2 border-t border-border-subtle pt-2 text-xs leading-snug text-text-tertiary">
        {t.legend.filterHint}
      </p>
    </FloatingPanel>
  );
}
